/**
 * M04 Skill/Tool 管理 shell · 清单 + 启停 + OAuth 健康 + 沙盒。
 */
import {
	CheckCircle2,
	Eye,
	Play,
	Power,
	RefreshCw,
	Save,
	ShieldCheck,
	XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { SingleStageRuntimePanel } from "@/components/shared/single-stage-runtime-panel";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
	type WriteActionItem,
	type WriteActionStatus,
	useConfirmWriteAction,
	useRejectWriteAction,
	useWriteActions,
} from "@/lib/api/runtime";
import type { SkillListItem } from "@/lib/api/skills";
import {
	useOAuthHealthCheck,
	usePatchSkillPrompt,
	useSkillDecisionPreview,
	useSkillPrompt,
	useSkillSandbox,
	useSkillsList,
	useToggleSkill,
} from "@/lib/api/skills";
import { skillLabel, skillTaobaoCoverage } from "@/lib/product-labels";

const CLASS_TONE: Record<string, string> = {
	"read-only": "border-brand-success/30 bg-brand-success/10 text-brand-success",
	write: "border-brand-warning/30 bg-brand-warning/10 text-brand-warning",
};

const RISK_TONE: Record<string, string> = {
	R0: "border-surface-border bg-surface-subtle text-text-secondary",
	R1: "border-brand-info/30 bg-brand-info/10 text-brand-info",
	R2: "border-brand-danger/30 bg-brand-danger/10 text-brand-danger",
};

const ACTION_VERIFY_PROFILES: Record<
	string,
	{
		inputSummary: string;
		verifyBoundary: string;
		resultExpectation: string;
		defaultSlots: Record<string, unknown>;
	}
> = {
	SK01: {
		inputSummary: "订单号、订单名、邮箱",
		verifyBoundary: "只读定位候选订单；订单权限未获批时应返回权限或空结果原因。",
		resultExpectation: "看是否返回候选订单、失败原因和 Shopify 证据来源。",
		defaultSlots: { order_id: "gid://shopify/Order/1001", email: "customer@example.com" },
	},
	SK02: {
		inputSummary: "订单 ID",
		verifyBoundary: "只读聚合履约、物流、退款和取消辅证，不修改订单。",
		resultExpectation: "看订单详情、履约状态和售后辅证是否能进入证据链。",
		defaultSlots: { order_id: "gid://shopify/Order/1001" },
	},
	SK03: {
		inputSummary: "商品 ID 或商品名称",
		verifyBoundary: "只读获取商品详情、变体、对比证据，支撑售前导购。",
		resultExpectation: "看商品事实、变体选项和证据来源是否完整。",
		defaultSlots: { product_name: "snowboard" },
	},
	SK04: {
		inputSummary: "queryKeys 至少 3 个、可选 filters",
		verifyBoundary: "只读返回商品推荐候选；不创建弃单挽回动作，不做写入。",
		resultExpectation: "看是否返回推荐候选、Shopify 证据来源或 queryKeys 缺失阻断。",
		defaultSlots: {
			queryKeys: ["black", "waterproof", "jacket"],
			filters: { price: { max: 200, currency: "USD" } },
		},
	},
	SK05: {
		inputSummary: "政策、FAQ 或知识问题",
		verifyBoundary: "只读检索知识库，返回政策知识、FAQ 或运营知识证据。",
		resultExpectation: "看命中文档、来源和知识摘要是否能进入后续回复证据。",
		defaultSlots: { query: "return policy" },
	},
	SK06: {
		inputSummary: "优惠码、折扣线索",
		verifyBoundary: "只读查询优惠规则和适用约束，不创建或修改优惠。",
		resultExpectation: "看优惠规则、适用条件和不可用原因是否清晰。",
		defaultSlots: { discount_code: "WELCOME10" },
	},
	SK07: {
		inputSummary: "订单 ID、取消原因、人工确认",
		verifyBoundary:
			"取消订单前必须人工确认；本页只验证 dry-run 保护链，不执行真实取消。当前只计划，不真实写店铺。",
		resultExpectation: "看是否保持 dry-run_only，并说明不会真实写店铺。",
		defaultSlots: {
			order_id: "gid://shopify/Order/1001",
			cancel_reason: "customer_request",
			confirmed: false,
		},
	},
	SK08: {
		inputSummary: "订单 ID、退货原因、行项目",
		verifyBoundary: "退货创建属于写操作；当前只计划，不真实写店铺。",
		resultExpectation: "看是否保持 dry-run_only，并保留后续确认和质量门要求。",
		defaultSlots: {
			order_id: "gid://shopify/Order/1001",
			return_reason: "wrong_size",
			line_item_id: "gid://shopify/LineItem/1",
		},
	},
	SK09: {
		inputSummary: "订单 ID、退款原因、金额",
		verifyBoundary: "退款执行属于高风险写操作；当前只计划，不真实写店铺。",
		resultExpectation: "看是否保持 dry-run_only，并提示需要确认、权限和审计链。",
		defaultSlots: {
			order_id: "gid://shopify/Order/1001",
			refund_reason: "customer_request",
			amount: "10.00",
		},
	},
	SK10: {
		inputSummary: "订单 ID、完整收货地址、人工确认",
		verifyBoundary: "修改地址属于写操作；当前只计划，不真实写店铺。",
		resultExpectation: "看是否保持 dry-run_only，并提示缺字段和确认链要求。",
		defaultSlots: {
			order_id: "gid://shopify/Order/1001",
			address1: "100 Main St",
			city: "New York",
			province: "NY",
			country: "US",
			zip: "10001",
			confirmed: false,
		},
	},
	SK11: {
		inputSummary: "转人工原因、会话摘要",
		verifyBoundary: "人工承接合同；当前只保留上下文交接边界，不创建真实坐席工单。",
		resultExpectation: "看是否保持 dry-run_only，并说明人工系统尚未真实接入。",
		defaultSlots: {
			handoff_reason: "customer_requested_agent",
			conversation_summary: "Customer asked for a human agent.",
		},
	},
};

function defaultSlots(skillId: string): string {
	return JSON.stringify(
		ACTION_VERIFY_PROFILES[skillId]?.defaultSlots ?? { order_id: "gid://shopify/Order/1001" },
		null,
		2,
	);
}

function defaultDecisionQuestion(skillId: string): string {
	if (skillId === "SK03") return "Gift Card价格多少";
	if (skillId === "SK04") return "你们店铺有什么可以推荐";
	if (skillId === "SK05") return "超过 30 天还能退货吗";
	if (skillId === "SK06") return "WELCOME10 还能用吗";
	if (skillId === "SK07") return "请帮我取消这个订单";
	if (skillId === "SK10") return "我要把这个订单地址改一下";
	return "查一下我的订单 1001";
}

function statusText(skill: SkillListItem): string {
	if (!skill.enabled) return "已停用";
	if (skill.pre_fetchable) return "可预取";
	if (skill.skill_class === "write" && skill.requires_dry_run) return "Dry-run 保护";
	if (skill.skill_class === "write") return "写操作";
	return "待接入";
}

function classText(skillClass: string): string {
	if (skillClass === "read-only") return "只读查询";
	if (skillClass === "write") return "写入动作";
	return skillClass;
}

function riskText(riskLevel: string): string {
	if (riskLevel === "R0") return "低风险（R0）";
	if (riskLevel === "R1") return "需确认（R1）";
	if (riskLevel === "R2") return "高风险（R2）";
	return riskLevel;
}

function executionBoundaryText(skill: SkillListItem): string {
	if (skill.pre_fetchable && skill.pre_fetch_trigger_slots.length) {
		return skill.pre_fetch_trigger_slots.join(" / ");
	}
	if (skill.requires_dry_run && skill.requires_confirmation) {
		return "dry-run / 用户确认 / Admin live gate";
	}
	if (skill.skill_id === "SK11") return "非 function tool / 转人工占位";
	return "—";
}

export type M04View = "catalog" | "action-verify" | "write-audit";

export function M04Shell({ view = "catalog" }: { view?: M04View }) {
	const skills = useSkillsList();
	const [selected, setSelected] = useState<string>("SK01");
	const [merchantId, setMerchantId] = useState("mer_devcons");
	const [slotJson, setSlotJson] = useState(defaultSlots("SK01"));
	const [slotError, setSlotError] = useState<string | null>(null);
	const [decisionQuestion, setDecisionQuestion] = useState(defaultDecisionQuestion("SK01"));
	const [decisionAllowedJson, setDecisionAllowedJson] = useState(JSON.stringify(["SK01"], null, 2));
	const [decisionSlotJson, setDecisionSlotJson] = useState(defaultSlots("SK01"));
	const [decisionError, setDecisionError] = useState<string | null>(null);
	const oauth = useOAuthHealthCheck();
	const sandbox = useSkillSandbox(selected);
	const decisionPreview = useSkillDecisionPreview(selected);
	const [writeStatusFilter, setWriteStatusFilter] = useState<WriteActionStatus | undefined>(
		"pending_confirmation",
	);
	const writeActions = useWriteActions(merchantId, writeStatusFilter);
	const confirmWriteAction = useConfirmWriteAction(merchantId);
	const rejectWriteAction = useRejectWriteAction(merchantId);
	const [selectedWriteActionId, setSelectedWriteActionId] = useState<string | null>(null);
	const [decisionMode, setDecisionMode] = useState<"confirm" | "reject" | null>(null);
	const [operator, setOperator] = useState("admin-console");
	const [rejectReason, setRejectReason] = useState("");
	const [writeActionStatus, setWriteActionStatus] = useState<string | null>(null);

	const rows = skills.data?.items ?? [];
	const selectedSkill = useMemo(
		() => rows.find((item) => item.skill_id === selected) ?? rows[0] ?? null,
		[rows, selected],
	);
	const writeActionItems = writeActions.data?.items ?? [];
	const selectedWriteAction = useMemo(
		() =>
			writeActionItems.find((item) => item.audit_id === selectedWriteActionId) ??
			writeActionItems[0] ??
			null,
		[writeActionItems, selectedWriteActionId],
	);

	useEffect(() => {
		if (writeActionItems.length === 0) {
			setSelectedWriteActionId(null);
			return;
		}
		if (
			!selectedWriteActionId ||
			!writeActionItems.some((item) => item.audit_id === selectedWriteActionId)
		) {
			setSelectedWriteActionId(writeActionItems[0]?.audit_id ?? null);
		}
	}, [selectedWriteActionId, writeActionItems]);

	function selectSkill(skillId: string) {
		setSelected(skillId);
		setSlotJson(defaultSlots(skillId));
		setDecisionSlotJson(defaultSlots(skillId));
		setDecisionAllowedJson(JSON.stringify([skillId], null, 2));
		setDecisionQuestion(defaultDecisionQuestion(skillId));
		setSlotError(null);
		setDecisionError(null);
	}

	function runDecisionPreview() {
		setDecisionError(null);
		let allowedSkills: string[];
		let extractedSlots: Record<string, unknown>;
		try {
			const rawAllowed = JSON.parse(decisionAllowedJson) as unknown;
			if (!Array.isArray(rawAllowed) || rawAllowed.some((item) => typeof item !== "string")) {
				setDecisionError("allowed_skills 必须是字符串数组");
				return;
			}
			allowedSkills = rawAllowed;
			const rawSlots = JSON.parse(decisionSlotJson) as unknown;
			if (rawSlots === null || typeof rawSlots !== "object" || Array.isArray(rawSlots)) {
				setDecisionError("已抽取参数必须是 JSON object");
				return;
			}
			extractedSlots = rawSlots as Record<string, unknown>;
		} catch (err) {
			setDecisionError(err instanceof Error ? err.message : String(err));
			return;
		}
		decisionPreview.mutate({
			merchant_id: merchantId,
			session_id: "m04_decision_preview",
			refined_query: decisionQuestion,
			allowed_skills: allowedSkills,
			extracted_slots: extractedSlots,
			semantic_task: selectedSkill?.skill_class === "write" ? "write_action_request" : null,
			selected_playbook_id:
				selectedSkill?.skill_class === "write" ? "PB04_cancel" : "PB06_pre_sale",
			selected_branch: selectedSkill?.skill_class === "write" ? "write_action" : "product_fact",
			history_summary: "M04 调试输入",
		});
	}

	function runSandbox() {
		setSlotError(null);
		let parsed: Record<string, unknown>;
		try {
			const raw = JSON.parse(slotJson) as unknown;
			if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
				setSlotError("slots 必须是 JSON object");
				return;
			}
			parsed = raw as Record<string, unknown>;
		} catch (err) {
			setSlotError(err instanceof Error ? err.message : String(err));
			return;
		}
		sandbox.mutate({
			merchant_id: merchantId,
			session_id: "m04_admin_sandbox",
			extracted_slots: parsed,
		});
	}

	function openWriteActionDecision(mode: "confirm" | "reject", item: WriteActionItem) {
		setSelectedWriteActionId(item.audit_id);
		setDecisionMode(mode);
		setWriteActionStatus(null);
		setOperator("admin-console");
		setRejectReason("");
	}

	function handleConfirmWriteAction() {
		if (!selectedWriteAction) return;
		setWriteActionStatus(null);
		confirmWriteAction.mutate(
			{
				auditId: selectedWriteAction.audit_id,
				idempotencyKey: selectedWriteAction.idempotency_key,
				confirmedBy: operator,
			},
			{
				onSuccess: (item) => {
					setWriteActionStatus(`已确认执行 · ${item.audit_id}`);
					setWriteStatusFilter(undefined);
					setSelectedWriteActionId(item.audit_id);
					setDecisionMode(null);
				},
				onError: (error) =>
					setWriteActionStatus(error instanceof Error ? error.message : "确认执行失败"),
			},
		);
	}

	function handleRejectWriteAction() {
		if (!selectedWriteAction) return;
		setWriteActionStatus(null);
		rejectWriteAction.mutate(
			{
				auditId: selectedWriteAction.audit_id,
				rejectedBy: operator,
				reason: rejectReason,
			},
			{
				onSuccess: (item) => {
					setWriteActionStatus(`已拒绝 · ${item.audit_id}`);
					setWriteStatusFilter(undefined);
					setSelectedWriteActionId(item.audit_id);
					setDecisionMode(null);
				},
				onError: (error) =>
					setWriteActionStatus(error instanceof Error ? error.message : "拒绝失败"),
			},
		);
	}

	if (skills.isLoading) {
		return (
			<div data-testid="m04-loading" className="p-4 text-sm text-text-secondary">
				加载工具清单中...
			</div>
		);
	}

	if (skills.isError) {
		return (
			<div data-testid="m04-error" className="p-4 text-sm text-brand-danger">
				加载失败：{skills.error instanceof Error ? skills.error.message : String(skills.error)}
			</div>
		);
	}

	return (
		<div className="space-y-4" data-testid="m04-shell">
			<div className="grid gap-3 md:grid-cols-4">
				<Metric label="Skill 总数" value={skills.data?.total ?? 0} />
				<Metric label="启用" value={skills.data?.enabled_count ?? 0} />
				<Metric
					label="只读 Skill"
					value={rows.filter((item) => item.skill_class === "read-only").length}
				/>
				<Metric
					label="写类 Skill"
					value={rows.filter((item) => item.skill_class === "write").length}
				/>
			</div>

			<div className="flex flex-wrap items-end gap-3 rounded-md border border-surface-border bg-surface-card p-3">
				<label className="grid gap-1 text-sm">
					<span className="text-xs text-text-muted">商户 ID</span>
					<input
						value={merchantId}
						onChange={(event) => setMerchantId(event.target.value)}
						className="h-9 w-56 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
						data-testid="m04-merchant-input"
					/>
				</label>
				<Button
					type="button"
					variant="outline"
					onClick={() => oauth.mutate({ merchant_id: merchantId, probe: false })}
					data-testid="m04-oauth-check"
				>
					<ShieldCheck className="mr-2 h-4 w-4" />
					检查 OAuth
				</Button>
				{oauth.data ? (
					<div className="text-sm text-text-secondary" data-testid="m04-oauth-result">
						<span className="font-mono">{oauth.data.shopify_domain}</span>
						<span className="ml-2">scopes {oauth.data.scopes_count}</span>
						<span className="ml-2">{oauth.data.oauth_state}</span>
					</div>
				) : null}
			</div>

			{view === "catalog" ? (
				<section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
					<div className="overflow-x-auto rounded-md border border-surface-border">
						<table
							className="w-full min-w-[840px] border-collapse text-sm"
							data-testid="m04-skill-table"
						>
							<thead className="bg-surface-subtle">
								<tr className="text-left">
									<th className="px-3 py-2 font-medium">Skill 能力包</th>
									<th className="px-3 py-2 font-medium">业务承接</th>
									<th className="px-3 py-2 font-medium">参数 / 触发</th>
									<th className="px-3 py-2 font-medium">绑定工具</th>
									<th className="px-3 py-2 font-medium">执行边界</th>
									<th className="px-3 py-2 font-medium">维护</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((skill) => (
									<SkillRow
										key={skill.skill_id}
										skill={skill}
										selected={selected === skill.skill_id}
										onSelect={selectSkill}
									/>
								))}
							</tbody>
						</table>
					</div>
					<SkillDetailPanel skill={selectedSkill} onSelect={selectSkill} />
				</section>
			) : null}

			{view === "write-audit" ? (
				<WriteAuditPanel
					items={writeActionItems}
					total={writeActions.data?.total ?? 0}
					isLoading={writeActions.isLoading}
					statusFilter={writeStatusFilter}
					setStatusFilter={setWriteStatusFilter}
					selectedItem={selectedWriteAction}
					selectItem={setSelectedWriteActionId}
					decisionMode={decisionMode}
					operator={operator}
					setOperator={setOperator}
					rejectReason={rejectReason}
					setRejectReason={setRejectReason}
					openDecision={openWriteActionDecision}
					onConfirm={handleConfirmWriteAction}
					onReject={handleRejectWriteAction}
					isConfirming={confirmWriteAction.isPending}
					isRejecting={rejectWriteAction.isPending}
					statusMessage={
						confirmWriteAction.isPending || rejectWriteAction.isPending
							? "处理中..."
							: writeActionStatus
					}
				/>
			) : null}

			{view === "action-verify" && selectedSkill ? (
				<div id="skill-action-verify" className="scroll-mt-6 space-y-4">
					<SkillDecisionPreviewPanel
						selectedSkill={selectedSkill}
						merchantId={merchantId}
						decisionQuestion={decisionQuestion}
						setDecisionQuestion={setDecisionQuestion}
						decisionAllowedJson={decisionAllowedJson}
						setDecisionAllowedJson={setDecisionAllowedJson}
						decisionSlotJson={decisionSlotJson}
						setDecisionSlotJson={setDecisionSlotJson}
						decisionError={decisionError}
						runDecisionPreview={runDecisionPreview}
						isPending={decisionPreview.isPending}
						result={decisionPreview.data ?? null}
					/>
					<section
						className="grid gap-4 rounded-md border border-surface-border p-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]"
						data-testid="m04-sandbox"
					>
						<div className="space-y-3">
							<div data-testid="m04-tool-sandbox">
								<h3 className="text-base font-semibold text-text-primary">绑定工具沙盒</h3>
								<p className="mt-1 text-sm text-text-secondary">
									这里验证 Skill 绑定的底层 handler、权限和 dry-run 返回；不代表前一步 Skill
									判断一定正确。
								</p>
							</div>
							<div className="grid max-w-md gap-1 text-sm">
								<span className="text-xs text-text-muted">选择 Skill</span>
								<Select
									className="w-full"
									aria-label="选择 Skill"
									value={selectedSkill.skill_id}
									onChange={(event) => selectSkill(event.target.value)}
									data-testid="m04-action-skill-select"
								>
									{rows.map((skill) => (
										<option key={skill.skill_id} value={skill.skill_id}>
											{skill.name}（{skill.skill_id}）
										</option>
									))}
								</Select>
							</div>
							<div>
								<h3 className="text-base font-semibold">
									{selectedSkill.name}（{selectedSkill.skill_id}）
								</h3>
								<p className="mt-1 text-sm text-text-secondary">{selectedSkill.description}</p>
								<p
									className="mt-2 rounded-md border border-surface-border bg-surface-subtle px-3 py-2 text-sm text-text-secondary"
									data-testid="m04-action-selected-boundary"
								>
									{ACTION_VERIFY_PROFILES[selectedSkill.skill_id]?.verifyBoundary ??
										"使用模拟参数验证当前工具边界。"}
								</p>
							</div>
							<textarea
								value={slotJson}
								onChange={(event) => setSlotJson(event.target.value)}
								className="min-h-40 w-full rounded-md border border-surface-border bg-surface-background p-3 font-mono text-xs"
								data-testid="m04-sandbox-input"
							/>
							{slotError ? <div className="text-sm text-brand-danger">{slotError}</div> : null}
							<Button type="button" onClick={runSandbox} data-testid="m04-sandbox-run">
								<Play className="mr-2 h-4 w-4" />
								运行绑定工具沙盒
							</Button>
						</div>
						<div className="rounded-md bg-surface-subtle p-3">
							<div className="mb-2 text-xs font-medium text-text-muted">沙盒结果</div>
							<pre
								className="max-h-72 overflow-auto whitespace-pre-wrap break-words text-xs"
								data-testid="m04-sandbox-result"
							>
								{sandbox.isPending
									? "运行中..."
									: sandbox.data
										? JSON.stringify(sandbox.data, null, 2)
										: "未运行"}
							</pre>
						</div>
					</section>
					<ActionVerifyMatrix rows={rows} selected={selected} onSelect={selectSkill} />
					<SingleStageRuntimePanel
						kind="tools"
						targetStage="tool_and_evidence"
						sampleId="P10TA005"
						sampleMessage="超过 30 天还能退货吗"
						expectedIntent="return_request"
						expectedPlaybook="PB03_return"
						assetName="兼容样本 / OAuth / 绑定工具 / 知识辅助"
						boundary="这是兼容的单样本回放，用来检查 OAuth、绑定工具沙盒和知识辅助；Skill 是否应执行以上方 Skill 判断测试为准。"
					/>
				</div>
			) : null}
		</div>
	);
}

function Metric({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card px-4 py-3">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="mt-1 font-mono text-2xl font-semibold">{value}</div>
		</div>
	);
}

function SkillDecisionPreviewPanel({
	selectedSkill,
	merchantId,
	decisionQuestion,
	setDecisionQuestion,
	decisionAllowedJson,
	setDecisionAllowedJson,
	decisionSlotJson,
	setDecisionSlotJson,
	decisionError,
	runDecisionPreview,
	isPending,
	result,
}: {
	selectedSkill: SkillListItem;
	merchantId: string;
	decisionQuestion: string;
	setDecisionQuestion: (value: string) => void;
	decisionAllowedJson: string;
	setDecisionAllowedJson: (value: string) => void;
	decisionSlotJson: string;
	setDecisionSlotJson: (value: string) => void;
	decisionError: string | null;
	runDecisionPreview: () => void;
	isPending: boolean;
	result: unknown | null;
}) {
	return (
		<section
			className="grid gap-4 rounded-md border border-surface-border bg-surface-card p-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]"
			data-testid="m04-skill-decision-preview"
		>
			<div className="space-y-3">
				<div>
					<h3 className="text-base font-semibold text-text-primary">Skill 判断测试</h3>
					<p className="mt-1 text-sm leading-6 text-text-secondary">
						验证 {selectedSkill.name} 是否应执行、会抽取哪些参数、生成哪个
						tool_plan；这里不调用底层工具。
					</p>
				</div>
				<label className="grid gap-1 text-sm">
					<span className="text-xs text-text-muted">客户问题</span>
					<input
						value={decisionQuestion}
						onChange={(event) => setDecisionQuestion(event.target.value)}
						className="h-9 rounded-md border border-surface-border bg-surface-background px-3 text-sm"
						data-testid="m04-decision-question"
					/>
				</label>
				<div className="grid gap-3 md:grid-cols-2">
					<label className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">allowed_skills</span>
						<textarea
							value={decisionAllowedJson}
							onChange={(event) => setDecisionAllowedJson(event.target.value)}
							className="min-h-28 rounded-md border border-surface-border bg-surface-background p-3 font-mono text-xs"
							data-testid="m04-decision-allowed"
						/>
					</label>
					<label className="grid gap-1 text-sm">
						<span className="text-xs text-text-muted">已抽取参数</span>
						<textarea
							value={decisionSlotJson}
							onChange={(event) => setDecisionSlotJson(event.target.value)}
							className="min-h-28 rounded-md border border-surface-border bg-surface-background p-3 font-mono text-xs"
							data-testid="m04-decision-slots"
						/>
					</label>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Button type="button" onClick={runDecisionPreview} data-testid="m04-decision-run">
						<Play className="mr-2 h-4 w-4" />
						运行 Skill 判断
					</Button>
					<span className="text-xs text-text-muted">merchant_id={merchantId}</span>
				</div>
				{decisionError ? <div className="text-sm text-brand-danger">{decisionError}</div> : null}
			</div>
			<div className="rounded-md bg-surface-subtle p-3">
				<div className="mb-2 text-xs font-medium text-text-muted">判断结果</div>
				<pre
					className="max-h-80 overflow-auto whitespace-pre-wrap break-words text-xs"
					data-testid="m04-decision-result"
				>
					{isPending ? "运行中..." : result ? JSON.stringify(result, null, 2) : "未运行"}
				</pre>
			</div>
		</section>
	);
}

function SkillDetailPanel({
	skill,
	onSelect,
}: {
	skill: SkillListItem | null;
	onSelect: (skillId: string) => void;
}) {
	if (!skill) {
		return (
			<aside className="rounded-md border border-surface-border bg-surface-card p-4 text-sm text-text-secondary">
				暂无 Skill 详情
			</aside>
		);
	}
	return (
		<aside
			className="rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="m04-skill-detail"
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-xs text-text-muted">当前选中 Skill</div>
					<h3 className="mt-1 text-base font-semibold text-text-primary">
						{skill.name}（{skill.skill_id}）
					</h3>
				</div>
				<span
					className={`rounded border px-2 py-1 text-xs ${
						CLASS_TONE[skill.skill_class] ?? CLASS_TONE["read-only"]
					}`}
				>
					{classText(skill.skill_class)}
				</span>
			</div>
			<p className="mt-3 text-sm leading-6 text-text-secondary">{skill.description}</p>
			<div className="mt-4 space-y-2 text-sm">
				<SkillDetailRow label="底层工具" value={skill.tool_id ?? "不注册 function tool"} />
				<SkillDetailRow label="业务承接" value={skillTaobaoCoverage(skill.skill_id)} />
				<SkillDetailRow label="触发参数" value={executionBoundaryText(skill)} />
				<SkillDetailRow label="风险等级" value={riskText(skill.risk_level)} />
				<SkillDetailRow
					label="维护状态"
					value={skill.enabled ? "已启用，可在本页停用" : "已停用，可在本页启用"}
				/>
				<SkillDetailRow label="Skill prompt" value="可维护，注入 call_SKxx 工具说明" />
				<SkillDetailRow label="参数判断" value="Prompt 指导参数挖掘，规则安全门兜底" />
			</div>
			<SkillPromptEditor skillId={skill.skill_id} />
			<div className="mt-4 flex flex-wrap gap-2">
				<Button
					type="button"
					size="sm"
					onClick={() => {
						onSelect(skill.skill_id);
						window.location.hash = "skill-action-verify";
					}}
				>
					<Play className="mr-1.5 h-3.5 w-3.5" />
					调试这个 Skill
				</Button>
			</div>
		</aside>
	);
}

function SkillPromptEditor({ skillId }: { skillId: string }) {
	const prompt = useSkillPrompt(skillId);
	const patchPrompt = usePatchSkillPrompt(skillId);
	const [template, setTemplate] = useState("");
	const [notes, setNotes] = useState("");

	useEffect(() => {
		if (!prompt.data) return;
		setTemplate(prompt.data.prompt_template);
		setNotes(prompt.data.notes ?? "");
	}, [prompt.data]);

	return (
		<section
			className="mt-4 rounded-md border border-surface-border p-3"
			data-testid="m04-skill-prompt-editor"
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-sm font-semibold text-text-primary">Skill prompt</div>
					<p className="mt-1 text-xs leading-5 text-text-secondary">
						可维护；注入主 Agent 的 call_SKxx 工具说明，用于指导参数挖掘和 tool_plan 生成。
					</p>
				</div>
				<span className="rounded border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success">
					{prompt.data?.source === "custom" ? "自定义" : "默认"}
				</span>
			</div>
			<textarea
				value={template}
				onChange={(event) => setTemplate(event.target.value)}
				className="mt-3 min-h-56 w-full rounded-md border border-surface-border bg-surface-background p-3 font-mono text-xs"
				data-testid="m04-skill-prompt-textarea"
			/>
			<label className="mt-2 grid gap-1 text-sm">
				<span className="text-xs text-text-muted">变更备注</span>
				<input
					value={notes}
					onChange={(event) => setNotes(event.target.value)}
					className="h-9 rounded-md border border-surface-border bg-surface-background px-3 text-sm"
					data-testid="m04-skill-prompt-notes"
				/>
			</label>
			<div className="mt-3 flex flex-wrap items-center gap-2">
				<Button
					type="button"
					size="sm"
					onClick={() =>
						patchPrompt.mutate({
							prompt_template: template,
							updated_by: "admin-console",
							notes,
						})
					}
					disabled={patchPrompt.isPending || !template.trim()}
					data-testid="m04-skill-prompt-save"
				>
					<Save className="mr-1.5 h-3.5 w-3.5" />
					保存 Skill prompt
				</Button>
				<span className="text-xs text-text-muted" data-testid="m04-skill-prompt-status">
					{patchPrompt.isPending
						? "保存中..."
						: patchPrompt.data
							? `已保存 · ${patchPrompt.data.updated_by ?? "system"}`
							: prompt.isLoading
								? "加载中..."
								: `planner_mode=${prompt.data?.planner_mode ?? "rule_guarded_prompt"}`}
				</span>
			</div>
		</section>
	);
}

function SkillDetailRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="grid gap-2 rounded-md bg-surface-subtle px-3 py-2 sm:grid-cols-[92px_minmax(0,1fr)]">
			<span className="text-xs text-text-muted">{label}</span>
			<span className="break-words text-text-primary">{value}</span>
		</div>
	);
}

function WriteAuditPanel({
	items,
	total,
	isLoading,
	statusFilter,
	setStatusFilter,
	selectedItem,
	selectItem,
	decisionMode,
	operator,
	setOperator,
	rejectReason,
	setRejectReason,
	openDecision,
	onConfirm,
	onReject,
	isConfirming,
	isRejecting,
	statusMessage,
}: {
	items: WriteActionItem[];
	total: number;
	isLoading: boolean;
	statusFilter: WriteActionStatus | undefined;
	setStatusFilter: (status: WriteActionStatus | undefined) => void;
	selectedItem: WriteActionItem | null;
	selectItem: (auditId: string) => void;
	decisionMode: "confirm" | "reject" | null;
	operator: string;
	setOperator: (value: string) => void;
	rejectReason: string;
	setRejectReason: (value: string) => void;
	openDecision: (mode: "confirm" | "reject", item: WriteActionItem) => void;
	onConfirm: () => void;
	onReject: () => void;
	isConfirming: boolean;
	isRejecting: boolean;
	statusMessage: string | null;
}) {
	return (
		<section
			id="write-actions"
			className="scroll-mt-6 space-y-4 rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="m04-write-audit"
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<h3 className="text-base font-semibold text-text-primary">写动作审计</h3>
					<p className="mt-1 max-w-3xl text-sm leading-6 text-text-secondary">
						查看主 Agent dry-run 后形成的 confirmation_plan、幂等键、Q gate 和 live
						执行结果；确认动作必须在这里留痕。
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<WriteStatusButton
						label="待确认写动作"
						active={statusFilter === "pending_confirmation"}
						onClick={() => setStatusFilter("pending_confirmation")}
						testId="m04-write-status-pending"
					/>
					<WriteStatusButton
						label="已执行"
						active={statusFilter === "live_executed"}
						onClick={() => setStatusFilter("live_executed")}
						testId="m04-write-status-live"
					/>
					<WriteStatusButton
						label="已拒绝"
						active={statusFilter === "rejected"}
						onClick={() => setStatusFilter("rejected")}
						testId="m04-write-status-rejected"
					/>
					<WriteStatusButton
						label="全部"
						active={statusFilter === undefined}
						onClick={() => setStatusFilter(undefined)}
						testId="m04-write-status-all"
					/>
				</div>
			</div>

			<div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
				<div className="overflow-x-auto rounded-md border border-surface-border">
					<table className="w-full min-w-[920px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">审计 ID</th>
								<th className="px-3 py-2 font-medium">Skill / 工具</th>
								<th className="px-3 py-2 font-medium">订单</th>
								<th className="px-3 py-2 font-medium">幂等键</th>
								<th className="px-3 py-2 font-medium">状态</th>
								<th className="px-3 py-2 font-medium">操作</th>
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
								<tr>
									<td colSpan={6} className="px-3 py-6 text-center text-text-secondary">
										加载写动作审计中...
									</td>
								</tr>
							) : items.length === 0 ? (
								<tr>
									<td colSpan={6} className="px-3 py-6 text-center text-text-secondary">
										当前筛选下暂无写动作审计
									</td>
								</tr>
							) : (
								items.map((item) => (
									<WriteActionAuditRow
										key={item.audit_id}
										item={item}
										selected={selectedItem?.audit_id === item.audit_id}
										onSelect={selectItem}
										openDecision={openDecision}
									/>
								))
							)}
						</tbody>
					</table>
				</div>

				<div className="space-y-3">
					<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm">
						<div className="flex items-center justify-between gap-3">
							<div>
								<div className="font-semibold text-text-primary">审计详情</div>
								<div className="text-xs text-text-muted">当前筛选 {total} 条</div>
							</div>
							{selectedItem ? (
								<WriteStatusPill status={selectedItem.status} />
							) : (
								<span className="text-xs text-text-muted">未选择</span>
							)}
						</div>
						<WriteActionDetail item={selectedItem} />
					</div>

					{selectedItem && decisionMode ? (
						<div
							className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3"
							data-testid="m04-write-action-decision"
						>
							<div className="text-sm font-semibold text-brand-warning">
								{decisionMode === "confirm" ? "确认 live write" : "拒绝写动作"}
							</div>
							<div className="mt-2 grid gap-2">
								<label className="grid gap-1 text-sm">
									<span className="text-xs text-brand-warning">操作人</span>
									<input
										value={operator}
										onChange={(event) => setOperator(event.target.value)}
										className="h-9 rounded-md border border-brand-warning/30 bg-white px-3 text-sm"
										data-testid="m04-confirmed-by"
									/>
								</label>
								{decisionMode === "reject" ? (
									<label className="grid gap-1 text-sm">
										<span className="text-xs text-brand-warning">拒绝原因</span>
										<input
											value={rejectReason}
											onChange={(event) => setRejectReason(event.target.value)}
											className="h-9 rounded-md border border-brand-warning/30 bg-white px-3 text-sm"
											data-testid="m04-reject-reason"
										/>
									</label>
								) : (
									<div className="rounded-md bg-white/70 px-3 py-2 font-mono text-xs text-brand-warning">
										idempotency_key={selectedItem.idempotency_key}
									</div>
								)}
							</div>
							<div className="mt-3 flex flex-wrap gap-2">
								{decisionMode === "confirm" ? (
									<Button
										type="button"
										size="sm"
										onClick={onConfirm}
										disabled={isConfirming || selectedItem.status !== "pending_confirmation"}
										data-testid="m04-confirm-write"
									>
										<CheckCircle2 className="mr-2 h-4 w-4" />
										确认执行
									</Button>
								) : (
									<Button
										type="button"
										size="sm"
										variant="danger"
										onClick={onReject}
										disabled={isRejecting || selectedItem.status !== "pending_confirmation"}
										data-testid="m04-reject-write"
									>
										<XCircle className="mr-2 h-4 w-4" />
										记录拒绝
									</Button>
								)}
							</div>
						</div>
					) : null}

					<div
						className="rounded-md bg-surface-subtle px-3 py-2 text-sm text-text-secondary"
						data-testid="m04-write-action-status"
					>
						{statusMessage ?? "选择审计记录查看详情，pending 状态可确认或拒绝。"}
					</div>
				</div>
			</div>
		</section>
	);
}

function WriteStatusButton({
	label,
	active,
	onClick,
	testId,
}: {
	label: string;
	active: boolean;
	onClick: () => void;
	testId: string;
}) {
	return (
		<Button
			type="button"
			size="sm"
			variant={active ? "accent" : "outline"}
			onClick={onClick}
			data-testid={testId}
		>
			{label}
		</Button>
	);
}

function WriteActionAuditRow({
	item,
	selected,
	onSelect,
	openDecision,
}: {
	item: WriteActionItem;
	selected: boolean;
	onSelect: (auditId: string) => void;
	openDecision: (mode: "confirm" | "reject", item: WriteActionItem) => void;
}) {
	return (
		<tr
			className={`border-t border-surface-border align-top ${selected ? "bg-brand-accent/5" : ""}`}
			data-testid={`m04-write-action-row-${item.audit_id}`}
		>
			<td className="px-3 py-3 font-mono text-xs text-text-primary">{item.audit_id}</td>
			<td className="px-3 py-3">
				<div className="font-medium text-text-primary">{skillLabel(item.skill_id)}</div>
				<div className="mt-1 font-mono text-xs text-text-muted">{item.tool_id}</div>
			</td>
			<td className="px-3 py-3 font-mono text-xs text-text-secondary">
				{shortRuntimeId(item.order_id)}
			</td>
			<td className="px-3 py-3 font-mono text-xs text-text-secondary">{item.idempotency_key}</td>
			<td className="px-3 py-3">
				<WriteStatusPill status={item.status} />
			</td>
			<td className="px-3 py-3">
				<div className="flex flex-wrap gap-2">
					<Button type="button" size="sm" variant="outline" onClick={() => onSelect(item.audit_id)}>
						<Eye className="mr-1.5 h-3.5 w-3.5" />
						详情
					</Button>
					<Button
						type="button"
						size="sm"
						disabled={item.status !== "pending_confirmation"}
						onClick={() => openDecision("confirm", item)}
						data-testid={`m04-open-confirm-${item.audit_id}`}
					>
						<CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
						确认
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						disabled={item.status !== "pending_confirmation"}
						onClick={() => openDecision("reject", item)}
						data-testid={`m04-open-reject-${item.audit_id}`}
					>
						<XCircle className="mr-1.5 h-3.5 w-3.5" />
						拒绝
					</Button>
				</div>
			</td>
		</tr>
	);
}

function WriteActionDetail({ item }: { item: WriteActionItem | null }) {
	if (!item) {
		return (
			<div className="mt-3 rounded-md bg-surface-subtle p-3 text-sm text-text-secondary">
				暂无审计详情
			</div>
		);
	}
	return (
		<div className="mt-3 space-y-3" data-testid="m04-write-action-detail">
			<div className="grid gap-2 sm:grid-cols-2">
				<DetailMetric label="状态" value={writeActionStatusLabel(item.status)} />
				<DetailMetric label="确认状态" value={item.confirmation_status} />
				<DetailMetric
					label="mutation"
					value={`mutation_executed=${String(item.mutation_executed)}`}
				/>
				<DetailMetric label="边界" value="主 Agent 不直写" />
			</div>
			<JsonBlock title="confirmation_plan" value={item.confirmation_plan} />
			<JsonBlock title="dry_run_action" value={item.dry_run_action} />
			<JsonBlock title="q_gate" value={item.q_gate} />
			<JsonBlock title="live_result" value={item.live_result ?? { status: "not_executed" }} />
			<div className="grid gap-1 rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
				<div>confirmed_by={item.confirmed_by ?? "未确认"}</div>
				<div>rejected_by={item.rejected_by ?? "未拒绝"}</div>
				<div>reject_reason={item.reject_reason ?? "无"}</div>
			</div>
		</div>
	);
}

function DetailMetric({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card p-2">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="mt-1 break-words font-mono text-xs font-semibold text-text-primary">
				{value}
			</div>
		</div>
	);
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
	return (
		<details className="rounded-md border border-surface-border bg-surface-subtle p-3" open>
			<summary className="cursor-pointer font-mono text-xs font-semibold text-text-primary">
				{title}
			</summary>
			<pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-xs text-text-secondary">
				{JSON.stringify(value, null, 2)}
			</pre>
		</details>
	);
}

function WriteStatusPill({ status }: { status: WriteActionStatus }) {
	const tone =
		status === "pending_confirmation"
			? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
			: status === "live_executed"
				? "border-brand-success/30 bg-brand-success/10 text-brand-success"
				: status === "live_failed"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";
	return (
		<span className={`inline-flex w-fit rounded-full border px-2 py-1 text-xs font-medium ${tone}`}>
			{writeActionStatusLabel(status)}
		</span>
	);
}

function writeActionStatusLabel(status: WriteActionStatus): string {
	if (status === "pending_confirmation") return "等待确认";
	if (status === "live_executed") return "已执行";
	if (status === "live_failed") return "执行失败";
	if (status === "rejected") return "已拒绝";
	if (status === "expired") return "已过期";
	return status;
}

function shortRuntimeId(value: string | null): string {
	if (!value) return "未返回";
	const parts = value.split("/");
	return parts[parts.length - 1] || value;
}

function ActionVerifyMatrix({
	rows,
	selected,
	onSelect,
}: {
	rows: SkillListItem[];
	selected: string;
	onSelect: (skillId: string) => void;
}) {
	return (
		<section
			className="scroll-mt-6 rounded-md border border-surface-border bg-surface-card"
			data-testid="m04-action-verify-matrix"
		>
			<div className="border-b border-surface-border px-4 py-3">
				<h3 className="text-base font-semibold text-text-primary">Skill 调试矩阵</h3>
				<p className="mt-1 text-sm text-text-secondary">
					逐项核验现有 SK01-SK11：先选 Skill，再分别运行 Skill 判断测试和绑定工具沙盒。
				</p>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[960px] border-collapse text-sm">
					<thead className="bg-surface-subtle">
						<tr className="text-left">
							<th className="px-3 py-2 font-medium">Skill / 绑定工具</th>
							<th className="px-3 py-2 font-medium">输入条件</th>
							<th className="px-3 py-2 font-medium">验证边界</th>
							<th className="px-3 py-2 font-medium">结果怎么看</th>
							<th className="px-3 py-2 font-medium">操作</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((skill) => {
							const profile = ACTION_VERIFY_PROFILES[skill.skill_id];
							const isSelected = selected === skill.skill_id;
							return (
								<tr
									key={skill.skill_id}
									className={`border-t border-surface-border align-top ${
										isSelected ? "bg-brand-accent/5" : ""
									}`}
									data-testid="m04-action-verify-row"
								>
									<td className="px-3 py-3" data-testid={`m04-action-verify-row-${skill.skill_id}`}>
										<div className="font-medium">
											{skill.name}（{skill.skill_id}）
										</div>
										<div className="mt-1 text-xs text-text-secondary">{skill.description}</div>
										<div className="mt-2 flex flex-wrap gap-1.5">
											{skill.tool_id ? (
												<span className="rounded border border-surface-border bg-surface-background px-2 py-0.5 font-mono text-xs text-text-muted">
													{skill.tool_id}
												</span>
											) : null}
											<span
												className={`rounded border px-2 py-0.5 text-xs ${
													CLASS_TONE[skill.skill_class] ?? CLASS_TONE["read-only"]
												}`}
											>
												{classText(skill.skill_class)}
											</span>
											<span
												className={`rounded border px-2 py-0.5 text-xs ${
													RISK_TONE[skill.risk_level]
												}`}
											>
												{riskText(skill.risk_level)}
											</span>
										</div>
									</td>
									<td className="px-3 py-3 text-text-secondary">
										{profile?.inputSummary ?? "按工具说明补充必要输入"}
									</td>
									<td className="px-3 py-3 text-text-secondary">
										{profile?.verifyBoundary ?? "使用模拟参数验证当前工具边界。"}
									</td>
									<td className="px-3 py-3 text-text-secondary">
										{profile?.resultExpectation ?? "看是否返回可复盘结果或明确阻断原因。"}
									</td>
									<td className="px-3 py-3">
										<Button
											type="button"
											size="sm"
											variant={isSelected ? "accent" : "outline"}
											onClick={() => onSelect(skill.skill_id)}
											data-testid={`m04-action-verify-select-${skill.skill_id}`}
										>
											选择调试
										</Button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function SkillRow({
	skill,
	selected,
	onSelect,
}: {
	skill: SkillListItem;
	selected: boolean;
	onSelect: (skillId: string) => void;
}) {
	const toggle = useToggleSkill(skill.skill_id);

	return (
		<tr
			className={`border-t border-surface-border align-top ${selected ? "bg-brand-accent/5" : ""}`}
			data-testid={`m04-skill-row-${skill.skill_id}`}
		>
			<td className="px-3 py-3">
				<button
					type="button"
					onClick={() => onSelect(skill.skill_id)}
					className="text-left hover:underline focus:outline-none focus:ring-2 focus:ring-brand-accent"
					data-testid={`m04-skill-select-${skill.skill_id}`}
				>
					<div className="font-medium">{skill.name}</div>
					<div className="mt-0.5 font-mono text-xs text-text-muted">{skill.skill_id}</div>
					<div className="max-w-md text-xs text-text-muted">{skill.description}</div>
					<div className="mt-2 flex flex-wrap gap-1.5">
						<span
							className={`rounded border px-2 py-0.5 text-xs ${
								CLASS_TONE[skill.skill_class] ?? CLASS_TONE["read-only"]
							}`}
						>
							{classText(skill.skill_class)}
						</span>
						<span className={`rounded border px-2 py-0.5 text-xs ${RISK_TONE[skill.risk_level]}`}>
							{riskText(skill.risk_level)}
						</span>
					</div>
				</button>
			</td>
			<td className="px-3 py-3 text-xs leading-5 text-text-secondary">
				{skillTaobaoCoverage(skill.skill_id)}
			</td>
			<td className="px-3 py-3 text-xs leading-5 text-text-secondary">
				{executionBoundaryText(skill)}
			</td>
			<td className="px-3 py-3">
				{skill.tool_id ? (
					<span className="rounded border border-surface-border bg-surface-background px-2 py-0.5 font-mono text-xs text-text-muted">
						{skill.tool_id}
					</span>
				) : (
					<span className="text-xs text-text-muted">不注册 function tool</span>
				)}
			</td>
			<td className="px-3 py-3 text-xs leading-5 text-text-secondary">
				<div>{skill.requires_dry_run ? "plan-only dry-run" : classText(skill.skill_class)}</div>
				<div>{skill.requires_confirmation ? "需要人工确认" : statusText(skill)}</div>
			</td>
			<td className="px-3 py-3">
				<div className={skill.enabled ? "mb-2 text-brand-success" : "mb-2 text-text-muted"}>
					{statusText(skill)}
				</div>
				<div className="flex flex-wrap gap-2">
					<Button
						type="button"
						size="sm"
						variant={skill.enabled ? "outline" : "accent"}
						onClick={() =>
							toggle.mutate({
								enabled: !skill.enabled,
								updated_by: "admin-console",
								reason: "M04 toggle",
							})
						}
						data-testid={`m04-skill-toggle-${skill.skill_id}`}
					>
						<Power className="mr-1.5 h-3.5 w-3.5" />
						{skill.enabled ? "停用" : "启用"}
					</Button>
					<Button
						type="button"
						size="sm"
						variant="ghost"
						onClick={() => {
							onSelect(skill.skill_id);
							window.location.hash = "skill-action-verify";
						}}
						data-testid={`m04-skill-sandbox-${skill.skill_id}`}
					>
						<RefreshCw className="mr-1.5 h-3.5 w-3.5" />
						沙盒
					</Button>
				</div>
			</td>
		</tr>
	);
}
